import { HandleType } from '@/enums/handle-type.enum';
import type { BaseNodeData } from '@/types/node';

/** Resolve handle type for a given side/key. Output handle maps follow the same v3 guard as {@link getHandleType}. */
export function handleTypeForKey(
  handles: BaseNodeData['handles'] | undefined,
  side: 'input' | 'output',
  key: string,
  version: number | undefined,
): HandleType {
  if (!handles) return HandleType.Any;
  if (side === 'output' && version !== 3) return HandleType.Any;
  const sideHandles = side === 'input' ? handles.input : handles.output;
  if (!sideHandles || Array.isArray(sideHandles)) return HandleType.Any;
  return sideHandles[key]?.type ?? HandleType.Any;
}

export const getHandleName = (handleId: string | null | undefined): string => {
  if (!handleId) return '';
  if (handleId.includes('-output-')) return handleId.split('-output-')[1];
  if (handleId.includes('-input-')) return handleId.split('-input-')[1];
  return '';
};

export const getHandleType = (handleId: string | null | undefined, node: any) => {
  if (!node || !node.data || !handleId) return HandleType.Any;
  let handleKey = '';
  let isOutput = false;

  if (handleId.includes('-output-')) {
    handleKey = handleId.split('-output-')[1];
    isOutput = true;
  } else if (handleId.includes('-input-')) {
    handleKey = handleId.split('-input-')[1];
    isOutput = false;
  }

  const { version, handles } = node.data;
  let handleType = HandleType.Any;

  if (handles && handleKey) {
    if (isOutput && handles.output && version === 3) {
      handleType = handles.output[handleKey]?.type;
    } else if (!isOutput && handles.input) {
      handleType = handles.input[handleKey]?.type;
    }
  }

  return handleType;
};
/** The New offer dark palette; canvas APIs need concrete colors rather than CSS variables. */
const darkHandleColors: Record<HandleType, string> = {
  [HandleType.Image]: '#97ffcd',
  [HandleType.Video]: '#ff7676',
  [HandleType.Audio]: '#00b6ff',
  [HandleType.ThreeDee]: '#8269ff',
  [HandleType.Text]: '#e28cf8',
  [HandleType.Mask]: '#e4ff97',
  [HandleType.Lora]: '#d249fc',
  [HandleType.Seed]: '#ff7237',
  [HandleType.Number]: '#ffc9c1',
  [HandleType.Boolean]: '#24cb71',
  [HandleType.Array]: '#33dfdf',
  [HandleType.KlingElement]: '#f9df90',
  [HandleType.Keyframe]: '#f2b50d',
  [HandleType.Any]: '#f3ffe3',
};

/** Concrete dark colors for the existing dark workflow thumbnail renderer. */
export const getHandleColorValue = (type: HandleType | undefined): string =>
  darkHandleColors[type && Object.hasOwn(darkHandleColors, type) ? type : HandleType.Any];

/** CSS colors inherit the nearest FPL color mode, including nested themes and live mode changes. */
export const getHandleColor = (type: HandleType | undefined): string => {
  const resolvedType = type && Object.hasOwn(darkHandleColors, type) ? type : HandleType.Any;
  return `var(--weave-handle-${resolvedType.toLowerCase()}, ${getHandleColorValue(resolvedType)})`;
};
